import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronLeft, FileText, Globe, Layers, LayoutGrid } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { getWorkspace, visibleWorkspaces, workspaceMatches } from './settingsNavConfig';
import type { SettingsWorkspace } from './settingsNavConfig';
import { isAdminRole } from '../../lib/roles';

/**
 * One entry per workspace. It links to the workspace's first visible tab and
 * stays selected on every tab — and every drill-in beneath a tab — so it
 * matches on the tab routes rather than on its own link — ALL of them, not
 * just the visible ones, so a direct visit to a tab this tenant is not shown
 * (/settings/trucking on a casing tenant) still marks where it lives.
 */
function SidebarNav({ workspaces, pathname }: { workspaces: SettingsWorkspace[]; pathname: string }) {
  return (
    <>
      {workspaces.map((ws) => {
        const active = workspaceMatches(getWorkspace(ws.key), pathname);
        return (
          <Link
            key={ws.key}
            to={ws.tabs[0].route}
            aria-current={active ? 'page' : undefined}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              active
                ? 'bg-fieldiq-accent/10 text-fieldiq-accent'
                : 'text-fieldiq-text hover:text-white hover:bg-fieldiq-border/50'
            }`}
          >
            <ws.icon size={16} />
            {ws.label}
          </Link>
        );
      })}
    </>
  );
}

export default function SettingsLayout() {
  const { hasVertical, user, company } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userIsAdmin = isAdminRole(user?.role);

  const workspaces = visibleWorkspaces({ hasVertical, isAdmin: userIsAdmin });

  const isIndex = location.pathname === '/settings' || location.pathname === '/settings/';

  /**
   * THE FOUR FIGURES COME FROM WHAT THE SURFACE ALREADY HOLDS — the workspace
   * list this component renders, and the profile `useAuth` already carries.
   * No query is added here, and nothing an endpoint returns is recomputed.
   *
   * THE SPLIT BETWEEN THE FIRST TWO AND THE LAST TWO IS THE EM-DASH RULE, and
   * it is not cosmetic. `hasVertical` answers `false` for every key when
   * `company` is absent, so a tenant whose profile did not arrive shows a
   * SHORTER list of workspaces and tabs — and that shorter list is exactly what
   * is on screen beside these figures, so counting it is honest whatever
   * happened. `Region` and `Verticals` are claims about the TENANT'S
   * CONFIGURATION rather than about the rendered surface, and with no company
   * there is nothing behind them: they go to an em dash rather than to `CA` and
   * `0`. A `0` there would read as "this tenant has no verticals enabled",
   * which is a fact about the data nobody measured.
   *
   * A TAX-RATE FIGURE WAS CONSIDERED AND REJECTED, because it could only ever
   * have been an em dash. `src/types/index.ts`'s `Company` declares `taxType`,
   * `taxRate` and `subdomain`, and GET /auth/me returns none of the three — it
   * sends id, name, displayName, country, logoUrl and
   * enabledVerticals (auth.routes.ts). A stat wired to a field that never
   * arrives is structurally absent, and no data could ever move it.
   *
   * These are not loading states. Routes below the auth gate do not mount until
   * `isLoading` is false (App.tsx), so `company` is settled — present, or
   * genuinely null — by the time this renders.
   */
  const visibleTabCount = workspaces.reduce((total, ws) => total + ws.tabs.length, 0);
  const verticals: string[] | null = company
    ? (company.enabledVerticals ?? company.enabled_verticals ?? [])
    : null;
  const country: string | null = company?.country ?? null;

  return (
    <div data-testid="settings-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* ONE BAND FOR THE WHOLE SURFACE, not one per workspace. It titles
          Settings; the workspace below keeps its own heading, because the band
          is naming where you are and the heading is naming which workspace is
          open. The arrow leaves Settings for the dashboard rather than stepping
          back through history: this band persists across every tab, and a
          history step from it would sometimes leave the surface and sometimes
          move one tab sideways — the same control doing two things depending on
          how you arrived. */}
      <PageHeaderBand
        eyebrow="Company configuration"
        title="Settings"
        onBack={() => navigate('/')}
        backLabel="Back to dashboard"
        stats={[
          {
            icon: <LayoutGrid className="size-4" aria-hidden="true" />,
            label: 'Workspaces',
            sub: 'Open to your role',
            tone: 'neutral',
            value: workspaces.length,
          },
          {
            icon: <FileText className="size-4" aria-hidden="true" />,
            label: 'Pages',
            sub: 'Tabs you can open',
            tone: 'neutral',
            value: visibleTabCount,
          },
          /* THIRD SLOT, so the label is held under the ~70px ceiling the
             circle's edge imposes between 1024 and 1048 (fe#698). "Region" is
             six characters; the sweep in the PR body has the measurement. */
          {
            icon: <Globe className="size-4" aria-hidden="true" />,
            label: 'Region',
            sub: 'Company country',
            tone: 'info',
            value: country ?? '—',
          },
          /* FOURTH SLOT, AND ITS LABEL IS A MEASUREMENT, NOT A PREFERENCE.
             "Verticals" is what the codebase calls these and it is the obvious
             word — but at nine characters the label ENDS where the circle's
             1px edge runs, and swept 1024->2560 against the two pages that
             shipped after their own sweep (Quotes, Alerts) it sat up to 0.41
             below their floor, at 13 widths. "Service Lines" is the same
             length as Quotes' own fourth label and brings that to 0.06 at 3
             widths — parity. The page's own vocabulary is untouched;
             `enabled_verticals`, `hasVertical` and every guard still say
             vertical. Shorten (or here, lengthen) the STAT, leave the names
             alone — fe#698's rule, applied to slot four. */
          {
            icon: <Layers className="size-4" aria-hidden="true" />,
            label: 'Service Lines',
            sub: 'Enabled here',
            tone: 'accent',
            value: verticals ? verticals.length : '—',
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto flex max-w-[1920px] gap-4">
          {/* Sidebar — 1024px and up only. Between 768 and 1023 the app's own
              sidebar is already drawn, and a second 224px column beside it left
              every settings page about 300px of a 592px main area on an 800px
              tablet. Below 1024 a tablet uses the phone pattern: the hub at
              /settings, then the page full width with the back strip below.
              SettingsIndexPage's redirect breakpoint must match this one. */}
          <nav className="hidden lg:block w-56 shrink-0 self-start rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-2 space-y-0.5">
            <SidebarNav workspaces={workspaces} pathname={location.pathname} />
          </nav>

          {/* Content. One panel, so the workspace reads as a card on the page
              rather than as loose copy against it — the body idiom every other
              re-skinned page uses. The pages inside carry their own `px-6 py-4`
              and their own sections; nothing about what they render or save
              moves. */}
          <div className="min-w-0 flex-1 overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
            {/* Back strip below 1024px — the sidebar is drawn only from 1024, so
                sub-pages need a way back to the hub. Not drawn on the hub itself (/settings). */}
            {!isIndex && (
              <div className="lg:hidden border-b border-fieldiq-border px-6 py-3">
                <button
                  onClick={() => navigate('/settings')}
                  className="flex items-center gap-3 text-fieldiq-muted hover:text-fieldiq-text"
                  aria-label="Back to Settings"
                >
                  <ChevronLeft className="h-5 w-5" />
                  <span className="text-sm font-medium">Settings</span>
                </button>
              </div>
            )}
            <Outlet />
          </div>
        </div>
      </section>
    </div>
  );
}
